import Box from "@mui/material/Box";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Globe, ArrowRight } from "lucide-react";

import MPHome from "./MPHome";
import MPHomeApps from "./BarberMainPageComps/MPHomeApps";
import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../App";
import { useAppContext } from "../AppContext";
import { useParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { useTranslation } from "react-i18next";

export default function BarberMainPage() {
  const { t, i18n } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  const {
    OpenDrawer,
    setOpenDrawer,
    setAllApps,
    Loading,
    setLoading,
    setChoosedDate,
    setChoosedApps,
    setAllData,
    setUser,
    User,
    setAppContextLoading,
    AppContextLoading,
    OtherBarbers,
    setOtherBarbers,
    refreshDataCallback,
    setValue,
    value,
    AllowUsersBookApp,
    setAllowUsersBookApp,
    WazeData,
    setWazeData,
  } = useAppContext();

  const { BarberPhoneNumber } = useParams();
  const hubRef = useRef(null);

  // Language selection logic - check on component mount
  useEffect(() => {
    try {
      const hasVisited = localStorage.getItem("hasVisited");
      const savedLang = localStorage.getItem("lang");

      if (!hasVisited) {
        setShowLanguageModal(true);
      } else if (savedLang) {
        // Parse the JSON string to get the actual value
        const parsedLang = JSON.parse(savedLang);
        i18n.changeLanguage(parsedLang);
      }
    } catch (error) {
      console.error("Error loading language from localStorage:", error);
      // If there's an error, clear localStorage and start fresh
      localStorage.removeItem("lang");
      localStorage.removeItem("hasVisited");
      setShowLanguageModal(true);
    }
  }, [i18n]);

  // Handle language selection from modal (first time visit)
  const handleLanguageSelect = (lang) => {
    try {
      i18n.changeLanguage(lang);
      // Store as JSON string: "ar" instead of ar
      localStorage.setItem("lang", JSON.stringify(lang));
      localStorage.setItem("hasVisited", "true");
      setShowLanguageModal(false);
    } catch (error) {
      console.error("Error saving language:", error);
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "he", name: "עברית", flag: "🇮🇱" },
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
  ];

  // ✅ Use useCallback to prevent infinite loops
  const GetAllApps = useCallback(() => {
    const data = {
      PhoneNumber: BarberPhoneNumber,
    };

    axios
      .post(`${MyDomain}Barbers2/GetAllBarberData`, data)
      .then((response) => {
        console.log("✅ Data fetched:", response.data);
        setAllowUsersBookApp(response.data.barberData.allowUsersBookApp);

        setAllApps(response.data.barberAppointments);
        setAllData(response.data);
        setWazeData(response.data.barberShopPlace);

        const allBarbersList = [];

        if (response.data.barberData) {
          allBarbersList.push(response.data.barberData);
        }

        if (response.data.allBarbers && response.data.allBarbers.length > 0) {
          allBarbersList.push(...response.data.allBarbers);
        }

        if (allBarbersList.length > 1) {
          setOtherBarbers(allBarbersList);
        }

        const today = moment().format("DD-MM-YYYY");
        const todaysAppointments = response.data.barberAppointments.filter(
          (app) => app.date === today,
        );

        setChoosedApps(todaysAppointments);
        setChoosedDate(moment().format("DD-MM-YYYY"));
        setLoading(true);
      })
      .catch((err) => {
        console.error("❌ Error fetching data:", err);
      });
  }, [BarberPhoneNumber]);

  // ✅ Initial fetch
  useEffect(() => {
    GetAllApps();
  }, [BarberPhoneNumber]);

  // ✅ SignalR setup
  useEffect(() => {
    if (!BarberPhoneNumber) {
      console.warn("⚠️ No BarberPhoneNumber available");
      return;
    }

    console.log("🔄 Setting up SignalR for barber:", BarberPhoneNumber);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000;
          }
          return null;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hubRef.current = connection;

    connection.onclose((error) => {
      console.log("🔌 BarberMainPage SignalR connection closed");
      if (error) {
        console.log("Reason:", error.message);
      }
    });

    connection.onreconnecting(() => {
      console.log("🔄 BarberMainPage SignalR reconnecting...");
    });

    connection.onreconnected((connectionId) => {
      console.log("✅ BarberMainPage SignalR reconnected! ID:", connectionId);
      GetAllApps();
    });

    const startConnection = async () => {
      try {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
          await connection.start();
          console.log("✅ BarberMainPage SignalR connected!");
          console.log("🔑 Connection ID:", connection.connectionId);

          connection.on("AppointmentCreated", (newAppointment) => {
            console.log("📩 AppointmentCreated:", newAppointment);
            GetAllApps();
            setOpenDrawer(false);
          });

          connection.on("ReceiveBookingUpdate", (updatedAppointment) => {
            console.log("📩 ReceiveBookingUpdate:", updatedAppointment);
            GetAllApps();
            setOpenDrawer(false);
          });
        }
      } catch (err) {
        console.error("❌ BarberMainPage SignalR connection error:", err);
        setTimeout(() => {
          if (connection.state === signalR.HubConnectionState.Disconnected) {
            startConnection();
          }
        }, 5000);
      }
    };

    startConnection();

    return () => {
      console.log("🧹 Cleaning up BarberMainPage SignalR...");

      if (connection) {
        connection.off("AppointmentCreated");
        connection.off("ReceiveBookingUpdate");

        const state = connection.state;
        if (
          state === signalR.HubConnectionState.Connected ||
          state === signalR.HubConnectionState.Connecting
        ) {
          connection
            .stop()
            .then(() => console.log("✅ BarberMainPage SignalR disconnected"))
            .catch((err) => {
              if (!err.message?.includes("connection being closed")) {
                console.log("⚠️ Error stopping SignalR:", err.message);
              }
            });
        }
      }
    };
  }, [BarberPhoneNumber, GetAllApps]);

  const navItems = [
    { label: `${t("TheMainPage")}`, icon: <HomeOutlinedIcon /> },
    { label: `${t("myappointments")}`, icon: <CalendarMonthOutlinedIcon /> },
  ];

  const loadUser = async () => {
    try {
      const value = await localStorage.getItem("User");
      if (value !== null) {
        setUser(JSON.parse(value));
        console.log("✅ User loaded!", JSON.parse(value));
      } else {
        console.log("⚠️ No user found in localStorage");
      }
    } catch (e) {
      console.error("❌ Failed to load user", e);
    } finally {
      setAppContextLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#fafbfc",
        position: "relative",
      }}
    >
      {/* Language Selection Modal (First Time Visit) */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 rounded-3xl border border-slate-700 max-w-md w-full p-8 shadow-2xl animate-slide-in">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-display font-bold mb-2 text-white">
                {t("WelcomeToNeholTorem") || "Welcome to NeholTorem! 👋"}
              </h2>
              <p className="text-slate-400">
                {t("SelectYourLanguage") ||
                  "Please select your preferred language"}
              </p>
            </div>

            <div className="space-y-3">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className="w-full p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-blue-500/50 hover:bg-slate-800 transition flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{lang.flag}</span>
                    <span className="text-lg font-semibold text-white">
                      {lang.name}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-400 group-hover:translate-x-1 transition" />
                </button>
              ))}
            </div>

            <p className="text-xs text-slate-500 text-center mt-6">
              {t("CanChangeLater") || "You can change this later from the menu"}
            </p>
          </div>
        </div>
      )}

      {/* Sophisticated Top Navigation */}
      {!OpenDrawer && (
        <Box
          sx={{
            position: "fixed",
            top: "16px",
            right: "16px",
            zIndex: 1100,
            animation: "slideInFromTop 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              padding: "5px",
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              borderRadius: "14px",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              boxShadow:
                "0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
            }}
          >
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(index);
                }}
                className={`nav-btn ${value === index ? "active" : ""}`}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px",
                  width: "48px",
                  height: "48px",
                  border: "none",
                  borderRadius: "10px",
                  backgroundColor: value === index ? "#1a1a1a" : "transparent",
                  color: value === index ? "#ffffff" : "#64748b",
                  cursor: "pointer",
                  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                  WebkitTapHighlightColor: "transparent",
                  outline: "none",
                  userSelect: "none",
                  overflow: "hidden",
                }}
                title={item.label}
              >
                {/* Animated background gradient for active state */}
                {value === index && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                      opacity: 1,
                      zIndex: 0,
                      animation: "fadeIn 0.3s ease",
                    }}
                  />
                )}

                {/* Glow effect on active */}
                {value === index && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "100%",
                      background:
                        "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                      zIndex: 0,
                      animation: "pulse 2s ease-in-out infinite",
                    }}
                  />
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 1,
                    transform: value === index ? "scale(1.1)" : "scale(1)",
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {React.cloneElement(item.icon, {
                    sx: {
                      fontSize: "20px",
                      transition: "all 0.3s ease",
                    },
                  })}
                </div>

                {/* Ripple effect container */}
                <div className="ripple-container" />
              </button>
            ))}
          </div>
        </Box>
      )}

      {Loading ? (
        <>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: value === 0 ? "block" : "none",
              }}
            >
              <MPHome />
            </div>
            <div
              style={{
                display: value === 1 ? "block" : "none",
              }}
            >
              <MPHomeApps />
            </div>
          </div>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #667eea",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <div
            style={{
              fontSize: "16px",
              color: "#6b7280",
              fontWeight: "500",
            }}
          >
            Loading...
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
          
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
          }

          @keyframes slide-in {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-slide-in {
            animation: slide-in 0.6s ease-out forwards;
          }
          
          /* Hover effects */
          .nav-btn:not(.active):hover {
            background-color: rgba(0, 0, 0, 0.04) !important;
            transform: scale(1.05);
          }
          
          .nav-btn:active {
            transform: scale(0.92);
          }
          
          /* Ripple effect */
          .nav-btn {
            position: relative;
            overflow: hidden;
          }
          
          .nav-btn::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }
          
          .nav-btn:active::before {
            width: 150px;
            height: 150px;
          }
          
          /* Mobile responsive adjustments */
          @media (max-width: 480px) {
            .nav-btn {
              width: 44px !important;
              height: 44px !important;
              padding: 8px !important;
            }
          }
          
          /* Smooth transitions for all interactive elements */
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `}
      </style>
    </div>
  );
}
